/**
 * Client-side offer-flow request builders — the boundary between the offer UI
 * (the `use-offer` realtime hook + `offer-checkout-sheet`) and the HTTP
 * routes under `app/api/offers/...`.
 *
 *   components/offers (sheet, modal) ── intent ──▶ builders here
 *   hooks/use-offer.ts                                  │
 *                                                       ├── safeParse (boundary)
 *                                                       └── fetch + JSON parse
 *
 * IMPORTANT — do NOT confuse with `lib/offers/offer-actions.ts`. That file
 * is the SERVER-side domain: it accepts a `SupabaseClient`, calls RPCs
 * (`place_offer`, `counter_offer`, …), fires email side-effects, and is
 * imported only by the `app/api/offers` route handlers. This file is the
 * CLIENT-side shell: it accepts intent-style args, runs the
 * `@kraahl/contract` Zod safeParse for body-carrying endpoints, then `fetch`es.
 *
 * Why this exists:
 *  - Inline `fetch('/api/offers/...')` calls were grandfathered inside
 *    `hooks/use-offer.ts` (G16) and `components/offers/offer-checkout-sheet.tsx`
 *    (G18) — see `.tondocs/FRONTEND_WIRING_STRATEGY.md §3.2`. The false-
 *    positive of "hex-ed" status came from the `lib/offers/offer-actions.ts`
 *    name collision; the postmortem `40fff2a8` exposed it. Sibling-D D2
 *    added compile-time `satisfies <ContractType>` pins on the body literals;
 *    this extract is the next step — pull body assembly + boundary safeParse
 *    into a lib/ builder so a Walking Skeleton test can drive producer +
 *    consumer in one call (no hand-coded body, no tautology).
 *  - Mobile (`apps/mobile/lib/api-offers.ts`) shares the place/counter wire
 *    contract; offer-settle is web-only today. Both web and mobile now read
 *    the same `@kraahl/contract` schemas instead of drifting by copy.
 *  - Component tests can mock `fetch` (the network seam) and let the real
 *    builder run — required to NOT regress to Coverage Theatre, the failure
 *    mode of `paypal-checkout.test.tsx` pre-S4 (mocking the builder
 *    short-circuits safeParse).
 *
 * Design notes:
 *  - Builders accept INTENT-style args (camelCase, what the caller has from
 *    state). Body assembly (snake_case wire format) is the builder's job —
 *    that's the entire point of the seam.
 *  - Place / counter / settle run `Schema.safeParse(body)` before fetch and
 *    throw `OfferContractError` on rejection — same shape as
 *    `lib/payments/checkout-actions.ts::CheckoutContractError` (programmer-
 *    bug signal; the server would 400 anyway, this surfaces it at the
 *    responsible line).
 *  - Accept / decline / withdraw have no Zod schema in `@kraahl/contract`
 *    today (decline carries `{ reason: string | null }`, the other two are
 *    URL-only). They keep the same `{ ok, status, data }` envelope for
 *    consumer symmetry. Introducing response schemas / new request schemas
 *    here is OUT of scope per `.tondocs/FRONTEND_WIRING_STRATEGY.md §1`.
 *  - Response shape is NOT yet pinned by Zod (same reasoning as
 *    `checkout-actions.ts`). Callers in `hooks/use-offer.ts` keep the same
 *    `{ ok, status, data }` consumer surface they had pre-extract.
 *
 * @see .tondocs/FRONTEND_WIRING_STRATEGY.md §3.1 / §3.2 G16 / G18
 * @see .tondocs/skills/hex.md §2 — Functional Core / Imperative Shell
 * @see lib/offers/offer-actions.ts — SERVER-side domain (do NOT confuse)
 * @see lib/payments/checkout-actions.ts — sibling pattern for the cart cluster
 * @see packages/contract/src/offers.ts — PlaceOfferRequestSchema / CounterOfferRequestSchema
 * @see packages/contract/src/offer-settle.ts — OfferSettleRequestSchema
 * @see apps/mobile/lib/api-offers.ts — mobile sibling using the same schemas
 */

import {
  CounterOfferRequestSchema,
  OfferSettleRequestSchema,
  PlaceOfferRequestSchema,
  type CounterOfferRequest,
  type OfferSettleRequest,
  type PlaceOfferRequest,
} from "@kraahl/contract"

// ---------------------------------------------------------------------------
// Shared error + result envelope
// ---------------------------------------------------------------------------

/**
 * Thrown when a request body fails the contract Zod parse before fetch.
 * Programmer-bug signal: the caller assembled an intent the contract does
 * not accept. The error message includes the route path so the failure is
 * greppable; details carry Zod's issue list for the responsible developer.
 * Mirrors `CheckoutContractError` from `lib/payments/checkout-actions.ts`.
 */
export class OfferContractError extends Error {
  readonly route: string
  readonly issues: ReadonlyArray<{ path: string; message: string }>
  constructor(
    route: string,
    issues: ReadonlyArray<{ path: string; message: string }>,
  ) {
    super(
      `[offer-builders] contract parse failed for ${route}: ${issues
        .map((i) => `${i.path}: ${i.message}`)
        .join("; ")}`,
    )
    this.name = "OfferContractError"
    this.route = route
    this.issues = issues
  }
}

/**
 * Envelope returned by every builder. Response shape is intentionally opaque
 * (`unknown`) — response schemas live as a separate response-side pass, see
 * file header. Hook callers keep their `{ ok, status, data }` consumer surface.
 */
export interface OfferBuilderResult {
  ok: boolean
  status: number
  data: unknown
}

async function readJson(res: Response): Promise<unknown> {
  return res.json().catch(() => null)
}

// ---------------------------------------------------------------------------
// G16 — placeOffer
// ---------------------------------------------------------------------------

export interface PlaceOfferIntent {
  listingId: string
  amount: number
  /**
   * Optional buyer note. Pass `undefined` or `null` if absent; the wire form
   * is `message: string | null` — `PlaceOfferRequestSchema` uses `.nullish()`
   * for this exact reason (see `packages/contract/src/offers.ts` notes and
   * the `40fff2a8` postmortem).
   */
  message?: string | null
  shippingMethodId?: string | null
  expiresInHours: number
  clientOfferId?: string
}

export async function placeOffer(
  intent: PlaceOfferIntent,
): Promise<OfferBuilderResult> {
  const body = {
    listing_id: intent.listingId,
    amount: intent.amount,
    message: intent.message ?? null,
    shipping_method_id: intent.shippingMethodId ?? null,
    expires_in_hours: intent.expiresInHours,
    client_offer_id: intent.clientOfferId,
  } satisfies PlaceOfferRequest

  const parsed = PlaceOfferRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new OfferContractError(
      "/api/offers",
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  const res = await fetch("/api/offers", {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}

// ---------------------------------------------------------------------------
// G16 — counterOffer
// ---------------------------------------------------------------------------

export interface CounterOfferIntent {
  parentOfferId: string
  amount: number
  message?: string | null
  expiresInHours: number
  clientOfferId?: string
}

export async function counterOffer(
  intent: CounterOfferIntent,
): Promise<OfferBuilderResult> {
  const body = {
    amount: intent.amount,
    message: intent.message ?? null,
    expires_in_hours: intent.expiresInHours,
    client_offer_id: intent.clientOfferId,
  } satisfies CounterOfferRequest

  const route = `/api/offers/${encodeURIComponent(intent.parentOfferId)}/counter`

  const parsed = CounterOfferRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new OfferContractError(
      route,
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  const res = await fetch(route, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}

// ---------------------------------------------------------------------------
// G18 — settleOffer
// ---------------------------------------------------------------------------

export interface SettleOfferIntent {
  offerId: string
  orderId: string
}

export async function settleOffer(
  intent: SettleOfferIntent,
): Promise<OfferBuilderResult> {
  const body = { orderId: intent.orderId } satisfies OfferSettleRequest
  const route = `/api/offers/${encodeURIComponent(intent.offerId)}/settle`

  const parsed = OfferSettleRequestSchema.safeParse(body)
  if (!parsed.success) {
    throw new OfferContractError(
      route,
      parsed.error.issues.map((i) => ({
        path: i.path.join("."),
        message: i.message,
      })),
    )
  }

  const res = await fetch(route, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}

// ---------------------------------------------------------------------------
// URL-only / no-schema endpoints — moved here for symmetry so the hook is a
// pure behavior layer (state + realtime refetch) calling lib builders.
// No boundary safeParse: no schema exists in `@kraahl/contract` today and
// inventing one for `{ reason }` / URL-only is out of scope per
// FRONTEND_WIRING_STRATEGY.md §1.
// ---------------------------------------------------------------------------

export async function acceptOffer(offerId: string): Promise<OfferBuilderResult> {
  const res = await fetch(`/api/offers/${encodeURIComponent(offerId)}/accept`, {
    method: "POST",
    credentials: "include",
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}

export async function declineOffer(
  offerId: string,
  reason?: string,
): Promise<OfferBuilderResult> {
  const res = await fetch(`/api/offers/${encodeURIComponent(offerId)}/decline`, {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason: reason ?? null }),
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}

export async function withdrawOffer(
  offerId: string,
): Promise<OfferBuilderResult> {
  const res = await fetch(`/api/offers/${encodeURIComponent(offerId)}/withdraw`, {
    method: "POST",
    credentials: "include",
  })
  return { ok: res.ok, status: res.status, data: await readJson(res) }
}
